import { useEffect, useState } from 'react';
import { Input } from '../../Input/Input';
import { Select } from '../../Select/Select';
import type { SelectOption } from '../../Select/Select.types';
import { WithholdingFormDialog } from './WithholdingFormDialog';
import { useWithholdingsTranslation } from './i18n';
import type { ModifyWithholdingPayload, WithholdingDto } from './types';

export interface WithholdingsEditModalProps {
  open: boolean;
  /** Fila a editar (provee valores iniciales y `rowVersion`). */
  row: WithholdingDto | null;
  /** Mutación en vuelo. */
  isSubmitting: boolean;
  onSubmit: (payload: ModifyWithholdingPayload) => void;
  onClose: () => void;
}

/**
 * Modal Modificar retención (FEAT-105-11.02).
 *
 * Pre-poblado con la fila seleccionada. Edita base/valor y la acción (1/2/3; el
 * backend rechaza 0/4). `rowVersion` viaja oculto en el payload para la
 * concurrencia optimista (un 409 → la lista se recarga).
 */
export function WithholdingsEditModal({
  open,
  row,
  isSubmitting,
  onSubmit,
  onClose,
}: WithholdingsEditModalProps) {
  const { t } = useWithholdingsTranslation();

  const [base, setBase] = useState('');
  const [total, setTotal] = useState('');
  const [action, setAction] = useState<1 | 2 | 3>(1);
  const [touched, setTouched] = useState(false);

  // Re-sembrar el formulario cuando cambia la fila / se abre el modal.
  useEffect(() => {
    if (open && row) {
      setBase(String(row.baseValue ?? ''));
      setTotal(String(row.totalValue ?? ''));
      setAction(row.withholdingAction === 2 ? 2 : row.withholdingAction === 3 ? 3 : 1);
      setTouched(false);
    }
  }, [open, row]);

  const actionOptions: SelectOption[] = [
    { value: 1, label: t('withholdings.action.1') },
    { value: 2, label: t('withholdings.action.2') },
    { value: 3, label: t('withholdings.action.3') },
  ];

  const baseNum = Number(base);
  const totalNum = Number(total);
  const baseValid = base.trim() !== '' && !Number.isNaN(baseNum) && baseNum > 0;
  const totalValid = total.trim() !== '' && !Number.isNaN(totalNum) && totalNum > 0;
  const formValid = baseValid && totalValid;

  function handleSubmit() {
    setTouched(true);
    if (!formValid || !row) return;
    onSubmit({
      baseValue: baseNum,
      totalValue: totalNum,
      withholdingAction: action,
      rowVersion: row.rowVersion,
    });
  }

  return (
    <WithholdingFormDialog
      open={open}
      title={t('withholdings.edit.title')}
      submitLabel={t('withholdings.edit.submit')}
      cancelLabel={t('withholdings.edit.cancel')}
      isSubmitting={isSubmitting}
      submitDisabled={!formValid}
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      <Input
        label={t('withholdings.edit.base')}
        type="number"
        min={0}
        step="any"
        inputMode="decimal"
        value={base}
        error={touched && !baseValid}
        errorMessage={touched && !baseValid ? t('withholdings.validation.basePositive') : undefined}
        onChange={(e) => setBase(e.target.value)}
      />

      <Input
        label={t('withholdings.edit.total')}
        type="number"
        min={0}
        step="any"
        inputMode="decimal"
        value={total}
        error={touched && !totalValid}
        errorMessage={touched && !totalValid ? t('withholdings.validation.totalPositive') : undefined}
        onChange={(e) => setTotal(e.target.value)}
      />

      <Select
        label={t('withholdings.edit.action')}
        options={actionOptions}
        value={action}
        onChange={(v) => setAction(Number(v) as 1 | 2 | 3)}
      />
    </WithholdingFormDialog>
  );
}
